import { useCallback } from "react";
import {
  ExceptionEventProperties,
  NavigationEventProperties,
  OperationEventProperties,
  OperationStatus,
  baseTrackExceptionEvent,
  baseTrackNavigationEvent,
  baseTrackOperationEvent,
} from "../Shared/AppInsights";
import { useEv2ExperimentParams } from "./UseCurrentExtension";
import { useSessionContext } from "../Contexts/SessionContext";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

/**
 * Tracks the lifecycle of an async operation (Started → Succeeded/Failed)
 * with a shared pageSessionId for correlation.
 */
export interface OperationTracker {
  /** The unique ID correlating all events for this operation instance */
  pageSessionId: string;
  /** Call before the async work begins. Emits a "Started" event. */
  start: (properties?: { [key: string]: any }) => void;
  /** Emit a step completion breadcrumb within this operation (e.g. "BranchCreated"). */
  step: (stepName: string, properties?: { [key: string]: any }) => void;
  /** Call when the async work completes successfully. Emits a "Succeeded" event. */
  succeed: (properties?: { [key: string]: any }) => void;
  /** Call when the async work fails. Emits a "Failed" event. */
  fail: (error?: string, properties?: { [key: string]: any }) => void;
}

/**
 * Custom hook for tracking Application Insights events with common properties
 * Will automatically include component, experiment, extension, and pageSessionId with each event.
 * @param component Name of the component that's tracking telemetry
 */
function useAppInsights(component: string = "Unknown") {
  const { pageSessionId = undefined } = useSessionContext() || {};
  const { experimentName = "Unknown", extensionName = "Unknown" } =
    useEv2ExperimentParams();

  /**
   * Tracks when a user navigates from one page/component to another
   */
  const trackNavigationEvent = useCallback(
    (properties: NavigationEventProperties) => {
      baseTrackNavigationEvent({
        component,
        ...properties,
        extensionName: extensionName,
        experimentName: experimentName,
        pageSessionId: pageSessionId,
      });
    },
    [component, extensionName, experimentName, pageSessionId],
  );

  /**
   * Tracks an operation event, such as a button click side affect of an action
   */
  const trackOperationEvent = useCallback(
    (properties: OperationEventProperties) => {
      baseTrackOperationEvent({
        ...properties,
        component,
        extensionName: extensionName,
        experimentName: experimentName,
        pageSessionId: pageSessionId,
      });
    },
    [component, extensionName, experimentName, pageSessionId],
  );

  /**
   * Track an exception, logs component, experiment, and extension context by default
   */
  const trackExceptionEvent = useCallback(
    (
      error: Error | string,
      severityLevel: SeverityLevel = SeverityLevel.Error,
      properties: ExceptionEventProperties,
    ) => {
      baseTrackExceptionEvent(error, severityLevel, {
        ...properties,
        component,
        extensionName: extensionName,
        experimentName: experimentName,
        pageSessionId: pageSessionId,
      });
    },
    [component, extensionName, experimentName, pageSessionId],
  );

  /**
   * Creates a tracker for an async operation that emits correlated
   * Started / Succeeded / Failed events with automatic duration.
   *
   * Usage:
   *   const tracker = createOperationTracker("Rollback", "handleStartRollback");
   *   tracker.start({ environment: "RC" });
   *   try {
   *     await doWork();
   *     tracker.succeed();
   *   } catch (err) {
   *     tracker.fail(err.message);
   *   }
   */
  const createOperationTracker = useCallback(
    (operation: string, functionName: string): OperationTracker => {
      const stepPageSessionId = pageSessionId || crypto.randomUUID();

      const emit = (
        status: OperationStatus,
        extra?: { [key: string]: any },
      ) => {
        baseTrackOperationEvent({
          operation,
          status,
          pageSessionId: stepPageSessionId,
          function: functionName,
          component,
          extensionName,
          experimentName,
          properties: extra,
        });
      };

      return {
        pageSessionId: stepPageSessionId,
        start: (properties?) => {
          emit(OperationStatus.Started, properties);
        },
        step: (stepName, properties?) => {
          emit(OperationStatus.Succeeded, { ...properties, step: stepName });
        },
        succeed: (properties?) => {
          emit(OperationStatus.Completed, properties);
        },
        fail: (error?, properties?) => {
          emit(OperationStatus.Failed, { ...properties, error });
        },
      };
    },
    [pageSessionId, component, extensionName, experimentName],
  );

  return {
    trackNavigationEvent,
    trackOperationEvent,
    trackExceptionEvent,
    createOperationTracker,
  };
}

export default useAppInsights;
